import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LogoMark } from "./Logo";

export const AuthModal = () => {
  const { modalOpen, closeAuth, modalTab, setModalTab, login, register, user } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (modalOpen) { 
      setError(null); 
      setSuccessMessage(null);
      setName("");
      setEmail("");
      setPassword("");
    }
  }, [modalOpen]);

  useEffect(() => {
    if (user && modalOpen && modalTab === "login") closeAuth();
  }, [user, modalOpen, modalTab, closeAuth]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    if (modalTab === "login") {
      const err = await login(email, password);
      setLoading(false);
      if (err) setError(err);
    } else {
      console.log("Invio nome al backend:", name); // Lo vedrai nella console del browser (F12)
      const err = await register(name, email, password);
      setLoading(false);
      if (err) {
        setError(err);
      } else {
        setSuccessMessage("Account creato con successo. Benvenuto in ALKEON.");
        setTimeout(() => {
          closeAuth();
        }, 2000);
      }
    }
  };

  return (
    <AnimatePresence>
      {modalOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}
          className="drawer-overlay z-[70]" onClick={closeAuth} data-testid="auth-overlay">
          <motion.div initial={{ opacity: 0.001, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0.001, y: 20 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()} className="auth-card" data-testid="auth-modal">
            <button onClick={closeAuth} data-testid="auth-close-btn" aria-label="Close"
              className="absolute top-5 right-5 text-stone hover:text-bone transition-colors duration-300">
              <X className="w-5 h-5" strokeWidth={1.2} />
            </button>
            
            <div className="text-center mb-10">
              <LogoMark className="w-8 h-8 text-sand mx-auto mb-5" />
              <p className="label">{modalTab === "login" ? "Welcome back" : "Join ALKEON"}</p>
            </div>

            <div className="flex gap-8 mb-8 border-b border-bone/10" data-testid="auth-tabs">
              {["login", "register"].map((tab) => (
                <button key={tab} onClick={() => { setModalTab(tab); setError(null); setSuccessMessage(null); }}
                  data-testid={`${tab}-tab`}
                  className={`pb-3 text-xs tracking-[0.25em] uppercase transition-colors duration-300 ${modalTab === tab ? "text-bone border-b border-bone" : "text-stone hover:text-bone"}`}>
                  {tab === "login" ? "Sign in" : "Create account"}
                </button>
              ))}
            </div>

            {successMessage ? (
              <div className="py-8 text-center space-y-3">
                <p className="text-sm font-serif text-sand tracking-wide">{successMessage}</p>
                <p className="text-xs text-stone font-mono">Ingresso nell'atelier in corso...</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-6" data-testid="auth-form">
                {modalTab === "register" && (
                  <input type="text" required value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Full name" aria-label="Full name" data-testid="register-name-input" className="auth-input" />
                )}
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address" aria-label="Email address" data-testid="auth-email-input" className="auth-input" />
                <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password (min. 8 characters)" aria-label="Password" data-testid="auth-password-input" className="auth-input" />
                {error && <p className="text-sm text-[#c98a7d] tracking-wide" data-testid="auth-error">{error}</p>}
                <button type="submit" disabled={loading} data-testid="auth-submit-btn" className="purchase-btn w-full justify-center">
                  <span className="relative z-10">{loading ? "One moment…" : modalTab === "login" ? "Sign in" : "Create account"}</span>
                </button>
              </form>
            )}

            <p className="text-xs text-stone tracking-wide mt-8 leading-relaxed text-center">
              Your cart travels with your account — new forms from the atelier, announced quietly.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};